import { HOST_PAYMENTS_PAYOUT_TERMS_VERSION } from 'cb-model';
import type { TermsDocument } from '../types';

export const hostPaymentsPayoutTerms: TermsDocument = {
  version: HOST_PAYMENTS_PAYOUT_TERMS_VERSION,
  lastUpdated: '8 April 2026',
  title: 'Careerbook Host Payments and Payout Terms',
  sections: [
    {
      title: 'Important notice',
      content: [
        {
          type: 'text',
          text: 'These Host Payments and Payout Terms apply to all Hosts using Careerbook to list, promote, sell, or receive money for Paid Events. They explain how payments, fees, refunds, payouts, reserves, chargebacks, and deductions work. By listing or selling a Paid Event through Careerbook, you agree to these Terms.',
        },
      ],
    },
    {
      title: '1. Scope and relationship to other terms',
      content: [
        {
          type: 'text',
          text: 'These Host Payments and Payout Terms form part of the agreement between you and Careerbook Enterprise Ltd (Careerbook, we, us, or our), together with the Careerbook Platform Terms of Service and any other applicable policies or signed agreements. If there is a conflict between these Terms and a separate written agreement signed by Careerbook, that signed agreement will prevail to the extent of the conflict.',
        },
      ],
    },
    {
      title: '2. Definitions',
      content: [
        { type: 'text', text: 'In these Terms:' },
        {
          type: 'bulleted',
          items: [
            'Attendee means a person who purchases, books, or registers for a Paid Event.',
            'Booking Fee means the Careerbook platform fee charged on or in relation to a Paid Booking.',
            'Chargeback means a payment reversal, card scheme dispute, issuer reversal, retrieval request, fraud claim, or similar reversal initiated through a bank, card issuer, payment provider, or payment scheme.',
            'Host means the person or entity listing, promoting, selling, or delivering the Paid Event.',
            'Net Proceeds means the sums due to the Host after deducting Booking Fees, payment processing charges, refunds, Chargebacks, reserves, taxes, withheld amounts, and any other lawful deductions.',
            'Paid Booking means a paid ticket, place, registration, or other paid access right to a Paid Event.',
            'Paid Event means an Event made available for payment through the Services.',
            "Payout means remittance of Net Proceeds to the Host's nominated payout account.",
            'Refund Policy means the cancellation and refund rules chosen by the Host for a Paid Event.',
            'Reserve means money that Careerbook temporarily withholds, sets aside, or delays paying out to cover actual or reasonably anticipated refunds, Chargebacks, claims, fraud, or other financial risk.',
          ],
        },
      ],
    },
    {
      title: '3. Eligibility to sell Paid Events',
      content: [
        { type: 'text', text: 'To sell Paid Events, a Host must:' },
        {
          type: 'bulleted',
          items: [
            'have a valid Careerbook account;',
            'provide all requested verification and onboarding information;',
            'provide an eligible payout account;',
            'comply with applicable law, payment provider rules, and Careerbook policies;',
            'provide truthful, complete, and not misleading event and business information.',
          ],
        },
        {
          type: 'text',
          text: 'Careerbook may approve, reject, suspend, or restrict access to paid event functionality at its reasonable discretion where necessary for legal, compliance, fraud, operational, or risk reasons.',
        },
      ],
    },
    {
      title: '4. Payment processing and collection',
      content: [
        {
          type: 'text',
          text: '4.1 Third-party payment providers\nCareerbook may use one or more third-party payment service providers to facilitate payments, settlement, verification, fraud controls, and payout operations.',
        },
        {
          type: 'text',
          text: '4.2 No representation of independent regulated status\nNothing in these Terms should be read as a statement that Careerbook is itself providing regulated payment services except to the extent legally authorised to do so. Relevant payment functions may be carried out by third-party payment service providers under their own legal and regulatory arrangements.',
        },
        {
          type: 'text',
          text: '4.3 Host authorisation\nBy listing a Paid Event, you authorise Careerbook and its payment service providers to:',
        },
        {
          type: 'bulleted',
          items: [
            'collect and receive attendee payments relating to your Paid Event;',
            'deduct applicable Booking Fees, processing charges, refunds, Chargebacks, reserves, and other lawful deductions;',
            'hold, route, settle, or remit funds as needed for platform operation;',
            'issue refunds in accordance with these Terms;',
            'recover sums owed by you by set-off, deduction, debit, reserve application, or other lawful means.',
          ],
        },
      ],
    },
    {
      title: '5. Booking Fees and charges',
      content: [
        {
          type: 'text',
          text: '5.1 Fees charged\nCareerbook may charge one or more of the following in connection with Paid Events:',
        },
        {
          type: 'bulleted',
          items: [
            'a Booking Fee;',
            'payment processing fees;',
            'subscription fees;',
            'onboarding or service fees where separately agreed;',
            'taxes where applicable.',
          ],
        },
        {
          type: 'text',
          text: '5.2 Disclosure\nCareerbook will aim to make applicable fees clear in the relevant product, pricing page, commercial proposal, or checkout flow.',
        },
        {
          type: 'text',
          text: '5.3 Fee changes\nWe may change fees from time to time on reasonable notice unless a separate signed agreement fixes your pricing for a defined period.',
        },
      ],
    },
    {
      title: '6. Host refund policies',
      content: [
        {
          type: 'text',
          text: '6.1 Host responsibility\nThe Host must set and publish a Refund Policy for each Paid Event. The Host is solely responsible for ensuring its Refund Policy is lawful, fair, transparent, and clearly disclosed before purchase.',
        },
        {
          type: 'text',
          text: '6.2 Specific-date leisure events and consumer rights\nMany Careerbook Events will be services related to leisure activities supplied on a specific date or within a specific period. Where that is the case, the usual 14-day distance-selling cancellation right may not apply under UK consumer law. However, Hosts remain responsible for ensuring their event terms and refund practices comply with all applicable law and for not misrepresenting attendee rights.',
        },
        {
          type: 'text',
          text: '6.3 No unfair terms\nA Host must not use refund or cancellation wording that is unfair, hidden, ambiguous, or misleading.',
        },
      ],
    },
    {
      title: '7. Refund mechanics',
      content: [
        {
          type: 'text',
          text: "7.1 Attendee cancellation within the Host's refund window\nIf an Attendee cancels within the period and conditions set out in the Host's Refund Policy and qualifies for a refund:",
        },
        {
          type: 'bulleted',
          items: [
            'the full ticket price, course fee, or other Host-set event price paid for that booking will be refunded to the Attendee; and',
            'the Careerbook Booking Fee will ordinarily not be refunded.',
          ],
        },
        {
          type: 'text',
          text: 'This is subject always to any non-excludable legal rights or any contrary statement expressly made by Careerbook at checkout or in writing.',
        },
        {
          type: 'text',
          text: '7.2 Host cancellation of a Paid Event\nIf a Host cancels a Paid Event, Careerbook may automatically process a full refund to affected Attendees, including:',
        },
        {
          type: 'bulleted',
          items: [
            'the full ticket price, course fee, or other Host-set event price; and',
            'the full Careerbook Booking Fee.',
          ],
        },
        {
          type: 'text',
          text: 'The Host authorises Careerbook to process those refunds automatically and without further notice where a Paid Event is cancelled.',
        },
        {
          type: 'text',
          text: "7.3 Event changes and legal obligations\nIf a Host materially changes an Event, applicable law may require the Host to offer a refund or another remedy even if the Host's standard Refund Policy states otherwise. The Host remains solely responsible for legal compliance in such circumstances.",
        },
        {
          type: 'text',
          text: '7.4 No rescheduling of Paid Events through the platform\nCareerbook does not currently support rescheduling of Paid Events through the platform. A Host must not attempt to use the platform to re-date a Paid Event in place of cancellation unless Careerbook expressly introduces that feature.',
        },
      ],
    },
    {
      title: '8. Payout timing',
      content: [
        {
          type: 'text',
          text: "8.1 Standard payout timing\nSubject to these Terms, Net Proceeds are normally paid automatically to the Host's configured payout account 7 days after the Event date.",
        },
        {
          type: 'text',
          text: '8.2 Preconditions for payout\nPayout may depend on:',
        },
        {
          type: 'bulleted',
          items: [
            'completion of identity, business, tax, or bank verification;',
            'compliance checks;',
            'fraud or risk review;',
            'absence of unresolved refunds, disputes, or Chargebacks;',
            'valid payout account details.',
          ],
        },
        {
          type: 'text',
          text: '8.3 Delayed or withheld payouts\nCareerbook may delay, withhold, split, cap, or suspend a Payout where reasonably necessary to manage refunds, Chargebacks, fraud concerns, legal compliance, sanctions, reserve requirements, or platform risk.',
        },
      ],
    },
    {
      title: '9. Reserves and set-off',
      content: [
        {
          type: 'text',
          text: "9.1 Reserves\nCareerbook may impose a Reserve where reasonably necessary in light of the Host's refund profile, event history, payment profile, Chargeback risk, fraud indicators, event size, event lead time, complaint patterns, or other risk factors.",
        },
        {
          type: 'text',
          text: '9.2 Use of reserves\nA Reserve may be used to satisfy or reduce refunds, Chargebacks, reversals, claims, bank losses, payment scheme losses, or other amounts the Host owes under these Terms.',
        },
        {
          type: 'text',
          text: '9.3 Set-off\nCareerbook may set off any sums owed by the Host against any present or future amounts otherwise payable to the Host.',
        },
      ],
    },
    {
      title: '10. Chargebacks, disputes, and reversals',
      content: [
        {
          type: 'text',
          text: "10.1 Host responsibility\nThe Host is financially responsible for Chargebacks, payment disputes, friendly fraud, unauthorised transaction claims, non-delivery claims, and similar reversals relating to its Paid Events, except to the extent caused by Careerbook's own breach of these Terms.",
        },
        {
          type: 'text',
          text: '10.2 Recovery rights\nIf a Chargeback or reversal occurs, Careerbook may:',
        },
        {
          type: 'bulleted',
          items: [
            'deduct the amount from current or future Payouts;',
            'apply any Reserve;',
            'invoice the Host;',
            're-debit an account where lawfully permitted;',
            'suspend paid event functionality or the Host Account;',
            'require additional verification or security measures.',
          ],
        },
        {
          type: 'text',
          text: '10.3 Evidence and cooperation\nThe Host must provide, promptly on request, all evidence reasonably required to respond to a dispute, including event descriptions, policies, attendee communications, access records, attendance logs, proof of delivery, and refund records.',
        },
        {
          type: 'text',
          text: '10.4 Administrative handling\nCareerbook may administer or assist with Chargeback responses, but is not obliged to contest every dispute and may act in the way it reasonably considers appropriate for operational, legal, or economic reasons.',
        },
      ],
    },
    {
      title: '11. Host warranties',
      content: [
        { type: 'text', text: 'The Host represents and warrants that:' },
        {
          type: 'bulleted',
          items: [
            'it has the legal right to list and sell the Event;',
            'the Event listing is accurate and not misleading;',
            'it will deliver the Event as described unless prevented by circumstances outside its reasonable control;',
            'its Refund Policy complies with law and is fair and transparent;',
            'it will not infringe third-party rights;',
            'it will comply with all applicable laws, regulations, and payment provider rules.',
          ],
        },
      ],
    },
    {
      title: '12. Taxes',
      content: [
        {
          type: 'text',
          text: 'Hosts are solely responsible for determining, charging, collecting, reporting, and remitting any taxes, VAT, duties, or similar amounts relating to their Events, unless applicable law requires otherwise. Careerbook may request tax information and may withhold or report amounts where required by law.',
        },
      ],
    },
    {
      title: '13. Prohibited activity and risk events',
      content: [
        { type: 'text', text: 'A Host must not:' },
        {
          type: 'bulleted',
          items: [
            'submit false or misleading business or event information;',
            'sell unlawful, fraudulent, or prohibited Events;',
            'use misleading pricing or hidden charges;',
            'direct attendees to bypass the platform in breach of these Terms;',
            'misuse refunds or cancellation tools;',
            'create an unreasonable risk of Chargebacks or complaints.',
          ],
        },
      ],
    },
    {
      title: '14. Suspension and termination',
      content: [
        {
          type: 'text',
          text: "Careerbook may restrict or terminate access to Paid Event functionality, suspend Payouts, or close a Host Account where reasonably necessary for legal, fraud, compliance, financial, operational, or reputational risk reasons. Where reasonably practicable, we will act proportionately, but immediate action may be taken where urgent action is needed. Termination does not affect Careerbook's right to recover sums owed, maintain reserves for a reasonable period, or process refunds and disputes arising from prior Events.",
        },
      ],
    },
    {
      title: '15. Liability and disclaimers',
      content: [
        {
          type: 'text',
          text: '15.1 Careerbook not responsible for Host event liabilities\nThe Host remains solely responsible for the Event, including cancellation, non-delivery, event changes, attendee claims, and all legally required refunds or remedies.',
        },
        {
          type: 'text',
          text: '15.2 Excluded losses\nTo the fullest extent permitted by law, Careerbook is not liable for indirect, incidental, special, or consequential losses, loss of profit, loss of revenue, loss of business, loss of goodwill, loss of opportunity, or losses arising from Chargebacks, payment provider actions, or event cancellation.',
        },
        {
          type: 'text',
          text: "15.3 Liability cap\nTo the fullest extent permitted by law, Careerbook's total aggregate liability under these Terms shall not exceed the total Booking Fees actually retained by Careerbook from the Host in relation to the Event or claim in the 3 months before the event giving rise to liability, or GBP 100 if greater.",
        },
        {
          type: 'text',
          text: '15.4 Non-excludable matters\nNothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud, fraudulent misrepresentation, or any liability that cannot lawfully be excluded.',
        },
      ],
    },
    {
      title: '16. Indemnity',
      content: [
        {
          type: 'text',
          text: 'The Host will indemnify Careerbook and its affiliates, officers, employees, and agents against claims, losses, liabilities, costs, and expenses, including reasonable legal fees, arising out of or connected with:',
        },
        {
          type: 'bulleted',
          items: [
            'the Event;',
            "the Host's Refund Policy;",
            'cancellation, non-delivery, or material change of the Event;',
            'breach of law;',
            'infringement of third-party rights;',
            "Chargebacks, refunds, claims, fines, or payment losses caused by the Host's acts or omissions.",
          ],
        },
        {
          type: 'text',
          text: "This indemnity does not apply to the extent caused by Careerbook's own negligence, fraud, or wilful misconduct.",
        },
      ],
    },
    {
      title: '17. Governing law and jurisdiction',
      content: [
        {
          type: 'text',
          text: 'These Terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over disputes arising from or connected with these Terms, except where mandatory law requires otherwise.',
        },
      ],
    },
  ],
};
